import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form as AntForm, Input, Space, Switch, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { FormProvider, useFieldArray, useForm, useFormContext, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import { DangerousActionModal, FormActions, FormField, FormSection } from '../../shared/form';
import { authFetch } from '../../utils/api';
import { canMutateSharedInfra } from '../../utils/permissions';

const hostSchema = z.string().trim().toLowerCase().regex(
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/,
  '請輸入網域名稱，不可包含 URL、port 或 IP',
);
const siteSchema = z.object({
  label: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, '僅可使用小寫英文、數字、-、_'),
  host: hostSchema,
  expectedBlocked: z.boolean(),
});
const uniqueSites = (name: string) => (sites: Array<{ label: string; host: string }>, ctx: z.RefinementCtx) => {
  const labels = new Set<string>();
  const hosts = new Set<string>();
  sites.forEach((site, index) => {
    const label = site.label.trim().toLowerCase();
    const host = site.host.trim().toLowerCase().replace(/\.$/, '');
    if (labels.has(label)) ctx.addIssue({ code: 'custom', path: [index, 'label'], message: `${name} label 不可重複` });
    if (hosts.has(host)) ctx.addIssue({ code: 'custom', path: [index, 'host'], message: `${name} host 不可重複` });
    labels.add(label); hosts.add(host);
  });
};
const ContentReachSchema = z.object({
  livestreamReachSites: z.array(siteSchema).min(1, '至少保留一個直播站').max(30, '直播站最多 30 個').superRefine(uniqueSites('直播站')),
  fiveSites: z.array(siteSchema).min(1, '至少保留一個強制 VPN 站').max(30, '強制 VPN 站最多 30 個').superRefine(uniqueSites('強制 VPN 站')),
  notes: z.string().max(256, '備註最多 256 字元').optional(),
}).superRefine((value, ctx) => {
  if (new TextEncoder().encode(JSON.stringify(value)).byteLength > 24 * 1024) {
    ctx.addIssue({ code: 'custom', path: [], message: '設定內容不可超過 24 KiB' });
  }
});
type Values = z.infer<typeof ContentReachSchema>;
type SiteListName = 'livestreamReachSites' | 'fiveSites';

const emptySite = (prefix: string) => ({ label: `${prefix}-${Date.now()}`, host: '', expectedBlocked: false });

function SiteListEditor({ name, title, description, prefix }: { name: SiteListName; title: string; description: string; prefix: string }) {
  const { control } = useFormContext<Values>();
  const { fields, append, remove } = useFieldArray({ control, name });
  return <FormSection title={title} description={description}>
    {fields.map((field, index) => <Card key={field.id} size="small" style={{ marginBottom: 12 }}>
      <Space direction="vertical" size={0} style={{ width: '100%' }}>
        <FormField<Values> name={`${name}.${index}.label` as FieldPath<Values>} label="Label" required control={<Input placeholder={`${prefix}-1`} />} />
        <FormField<Values> name={`${name}.${index}.host` as FieldPath<Values>} label="Host" required control={<Input placeholder="www.example.com" />} />
        <FormField<Values> name={`${name}.${index}.expectedBlocked` as FieldPath<Values>} label="預期被牆（待實測）" valuePropName="checked" control={<Switch />} />
        <Button danger icon={<DeleteOutlined />} onClick={() => remove(index)} disabled={fields.length <= 1}>移除此站點</Button>
      </Space>
    </Card>)}
    <Button icon={<PlusOutlined />} onClick={() => append(emptySite(prefix))}>新增站點</Button>
  </FormSection>;
}

export const ContentReachConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [version, setVersion] = useState<number | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(ContentReachSchema),
    defaultValues: { livestreamReachSites: [], fiveSites: [], notes: '' },
  });
  const canEdit = canMutateSharedInfra();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch('/api/admin/content-reach-config');
      const result = await response.json();
      if (result.code) throw new Error(result.msg);
      form.reset({ livestreamReachSites: result.data.livestreamReachSites, fiveSites: result.data.fiveSites, notes: '' });
      setVersion(result.data.version);
    } catch (error) {
      message.error((error as Error).message || '載入內容可達設定失敗');
    } finally { setLoading(false); }
  }, [form]);
  useEffect(() => { void load(); }, [load]);

  const save = async (values: Values) => {
    setSaving(true);
    try {
      const response = await authFetch('/api/admin/content-reach-config', { method: 'PUT', body: JSON.stringify(values) });
      const result = await response.json();
      if (result.code) throw new Error(result.msg);
      form.reset({ livestreamReachSites: result.data.livestreamReachSites, fiveSites: result.data.fiveSites, notes: '' });
      setVersion(result.data.version);
      setConfirmOpen(false);
      message.success(`內容可達設定已儲存（版本 ${result.data.version}）`);
    } catch (error) {
      message.error((error as Error).message || '儲存內容可達設定失敗');
    } finally { setSaving(false); }
  };

  return <div className="page-enter"><Card title={<Space>內容可達設定 {version !== null && <Tag color="blue">版本 {version}</Tag>}</Space>} loading={loading}>
    <BrandScopeNotice kind="shared-infra" style={{ marginBottom: 16 }} />
    <Alert showIcon type="warning" style={{ marginBottom: 16 }} message="此設定會下發到 App" description="儲存後會立即失效 Redis 快取；若 Redis 暫時不可用，App 最多可能看到舊設定 5 分鐘。請先確認 host 與站點用途。" />
    <FormProvider {...form}><AntForm layout="vertical" onFinish={form.handleSubmit(() => setConfirmOpen(true))}>
      <SiteListEditor name="livestreamReachSites" title="直播站探測清單" description="用於 content-plane reach probe。每份清單內 label 與 host 不可重複。" prefix="live" />
      <SiteListEditor name="fiveSites" title="強制 VPN 五站清單" description="供無敵星星智能模式判斷強制 VPN 目標。" prefix="five" />
      <FormSection title="操作備註"><FormField<Values> name="notes" label="變更原因" help="會寫入設定與審計紀錄。" control={<Input.TextArea rows={3} maxLength={256} showCount />} /></FormSection>
      {!canEdit && <Alert type="info" showIcon message="只有全品牌管理員可儲存共享設定" style={{ marginTop: 16 }} />}
      {canEdit && <FormActions submitting={saving} dirty={form.formState.isDirty} submitLabel="驗證並儲存" onCancel={() => void load()} />}
    </AntForm></FormProvider>
    <DangerousActionModal open={confirmOpen} title="確認更新內容可達設定" warning="此操作會讓新設定下發到 App，請再次確認所有 host 正確。" confirmKeyword="UPDATE" confirmHint={<Typography.Text>請輸入 <Typography.Text code>UPDATE</Typography.Text> 確認更新：</Typography.Text>} confirming={saving} confirmLabel="確認更新" onCancel={() => setConfirmOpen(false)} onConfirm={() => void save(form.getValues())} />
  </Card></div>;
};
